import React, {useEffect, useState} from 'react';
import Pagination from 'react-bootstrap/Pagination';
import {Dropdown} from 'react-bootstrap';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const CustomPagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [selectedPage, setSelectedPage] = useState<string>('Select');

  useEffect(() => {
    if (currentPage <= 3 || currentPage == totalPages) {
      setSelectedPage('Select');
    } else {
      setSelectedPage(currentPage.toString());
    }
  }, [currentPage, totalPages]);

  const handlePageSelect = (pageNumber: number) => {
    setSelectedPage(pageNumber.toString()); // Update dropdown label
    onPageChange(pageNumber); // Trigger page change
  };

  const renderPaginationItems = (): JSX.Element[] => {
    let items: JSX.Element[] = [];

    if (totalPages > 5) {
      // Show first three pages
      for (let number = 1; number <= 3; number++) {
        items.push(
          <Pagination.Item
            key={number}
            active={number === currentPage}
            onClick={() => onPageChange(number)}
          >
            {number}
          </Pagination.Item>,
        );
      }

      // Dropdown for middle pages
      items.push(
        <Dropdown className='dropdown_auto' key='dropdown'>
          <Dropdown.Toggle className='px-2 custom-dropdown'>
            {selectedPage} {/* Show selected page instead of "Select" */}
          </Dropdown.Toggle>
          <Dropdown.Menu className='custom-scroll-menu'>
            {Array.from({length: totalPages - 4}, (_, index) => {
              const pageNumber = index + 4;
              return (
                <Dropdown.Item
                  key={pageNumber}
                  onClick={() => handlePageSelect(pageNumber)}
                >
                  {pageNumber}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>,
      );

      // Show last page
      items.push(
        <Pagination.Item
          key={totalPages}
          active={totalPages === currentPage}
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    } else {
      // Show all pages if total is 5 or less
      for (let number = 1; number <= totalPages; number++) {
        items.push(
          <Pagination.Item
            key={number}
            active={number === currentPage}
            onClick={() => onPageChange(number)}
          >
            {number}
          </Pagination.Item>,
        );
      }
    }

    return items;
  };

  return (
    <div className='d-inline-block ms-3'>
      <Pagination className='mt-0'>
        <Pagination.Prev
          onClick={() => {
            onPageChange(1);
            setSelectedPage('Select');
          }}
          disabled={currentPage === 1}
        >
          {'<<'}
        </Pagination.Prev>

        <Pagination.Prev
          onClick={() => {
            const newPage = Math.max(currentPage - 1, 1);
            onPageChange(newPage);
            setSelectedPage(newPage > 3 ? newPage.toString() : 'Select');
          }}
          disabled={currentPage === 1}
        >
          {'<'}
        </Pagination.Prev>

        {renderPaginationItems()}

        <Pagination.Next
          onClick={() => {
            const newPage = Math.min(currentPage + 1, totalPages);
            onPageChange(newPage);
            setSelectedPage(newPage > 3 ? newPage.toString() : 'Select');
          }}
          disabled={currentPage === totalPages}
        >
          {'>'}
        </Pagination.Next>

        <Pagination.Next
          onClick={() => {
            onPageChange(totalPages);
            setSelectedPage(totalPages.toString());
          }}
          disabled={currentPage === totalPages}
        >
          {'>>'}
        </Pagination.Next>
      </Pagination>
    </div>
  );
};

export default CustomPagination;
